import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GAME_CONSTANTS } from '@utils/constants';

// Stores y APIs
import useAuthStore from '@store/useAuthStore';
import useCriticalErrorStore from '@store/useCriticalErrorStore';
import { getLobbyState, setReadyStatus, startGame, updateBotConfig, getAILoadoutTemplates } from '@/api/lobbyApi';
import { PlayerStatusInGame } from '@utils/enums';
import feedback from '@/components/ui/general/feedback/feedback';


export function useLobby({ onCountdownEnd }) {
  const { gameId } = useParams();
  const queryClient = useQueryClient();
  const { activePlayer } = useAuthStore();
  const setCriticalError = useCriticalErrorStore((state) => state.setCriticalError);

  const [isStarting, setIsStarting] = useState(false);
  const [countdown, setCountdown] = useState(GAME_CONSTANTS.LOBBY_COUNTDOWN);

  const { data: lobbyState, isLoading, error } = useQuery({
    queryKey: ['lobby', gameId],
    queryFn: () => getLobbyState(gameId),
    refetchInterval: 5000,
    enabled: !isStarting,
  });

  const readyMutation = useMutation({
    mutationFn: ({ isReady }) => setReadyStatus(gameId, isReady),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
    },
  });

  const startMutation = useMutation({
    mutationFn: () => startGame(gameId),
    onSuccess: (data) => {
      // Si el backend retorna warning, NO iniciar el juego
      if (data?.status === 'warning') {
        console.warn('[useLobby] Start game returned warning:', data.userMessage);
        // El toast ya se muestra automáticamente por el interceptor
        return;
      }

      // Solo si es success real, iniciar countdown
      if (data?.status === 'success') {
        setIsStarting(true);
      }
    },
    onError: () => {
      // Don't set isStarting to true on error
      setIsStarting(false);
      // Error logging and feedback handled centrally by api.js
    },
  });

  const botConfigMutation = useMutation({
    mutationFn: (config) => updateBotConfig(gameId, config),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
      feedback.success('Configuración del bot actualizada');
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Fetch AI loadout templates (for BotConfigModal)
  const { data: aiLoadoutData, isLoading: isAILoadoutLoading } = useQuery({
    queryKey: ['ai-loadout-templates', gameId],
    queryFn: () => getAILoadoutTemplates(gameId),
  });

  // const startMutation = useMutation({
  //   mutationFn: () => startGame(gameId),
  //   onSuccess: () => {
  //     setIsStarting(true);
  //   },
  // });

  // Efecto que maneja el contador
  useEffect(() => {
    if (!isStarting) return;
    setCountdown(GAME_CONSTANTS.LOBBY_COUNTDOWN); // Reinicia el contador
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 350);
    return () => clearInterval(timer);
  }, [isStarting]);

  // Efecto que reacciona al final de la cuenta atrás
  useEffect(() => {
    if (countdown === 0) {
      if (onCountdownEnd) {
        onCountdownEnd();
      }
    }
    // ✅ CORRECCIÓN 2: Añadimos 'onCountdownEnd' a las dependencias.
  }, [countdown, onCountdownEnd]);

  // Lógica y datos derivados para la UI

  // ✅ CORRECCIÓN 1: Memorizamos la variable 'players'.
  const players = useMemo(() => lobbyState?.players || [], [lobbyState?.players]);

  // Player rosters (units for each player in lobby)
  const playerRosters = useMemo(() => lobbyState?.player_rosters || {}, [lobbyState?.player_rosters]);

  // ✅ VALIDACIÓN CRÍTICA: Verificar que todos los players tengan status_in_game
  // FAIL LOUDLY - No usar fallbacks que oculten datos faltantes
  useEffect(() => {
    if (players.length > 0) {
      const playersWithMissingStatus = players.filter(p => p.pivot?.status_in_game === undefined || p.pivot?.status_in_game === null);
      if (playersWithMissingStatus.length > 0) {
        setCriticalError('Lobby data is incomplete: missing player status', {
          source: 'useLobby',
          details: playersWithMissingStatus.map(p => ({ id: p.id, name: p.name, pivot: p.pivot }))
        });
      }
    }
  }, [players, setCriticalError]);

  const currentUserPlayer = useMemo(() => players.find(p => p.id === activePlayer?.id), [players, activePlayer]);
  const isCreator = useMemo(() => currentUserPlayer?.pivot?.is_creator, [currentUserPlayer]);
  const allHumanPlayersReady = useMemo(() => players.filter(p => p.type === "human").every(p => p.pivot.status_in_game === PlayerStatusInGame.LOBBIED), [players]);

  // Handlers que la vista llamará
  const handleReadyClick = () => {
    if (!currentUserPlayer) return;
    const currentStatus = currentUserPlayer.pivot.status_in_game;
    const newReadyState = currentStatus !== PlayerStatusInGame.LOBBIED;
    readyMutation.mutate({ isReady: newReadyState });
  };

  const handleStartGameClick = () => {
    startMutation.mutate();
  };

  const handleUpdateBotConfig = useCallback((config) => {
    botConfigMutation.mutate(config);
  }, [botConfigMutation]);

  // Get AI player from lobby
  const aiPlayer = useMemo(() => players.find(p => p.type === 'ai'), [players]);

  // Get current bot config from AI player's pivot properties
  // Default to 'random' if no config exists (matches backend default)
  const currentBotConfig = useMemo(() => {
    return aiPlayer?.pivot?.properties?.bot_config || {
      spawn_mode: 'grouped',
      unit_combo: 'random',
    };
  }, [aiPlayer]);

  // AI loadout templates for BotConfigModal
  const aiLoadoutTemplates = useMemo(() => aiLoadoutData?.loadout_templates || [], [aiLoadoutData]);

  // Devolvemos todo lo que la vista necesita
  return {
    isLoading,
    isStarting,
    error,
    countdown,
    lobbyState,
    players,
    playerRosters,
    currentUserPlayer,
    isCreator,
    allHumanPlayersReady,
    handleReadyClick,
    handleStartGameClick,
    // Bot config
    aiPlayer,
    currentBotConfig,
    handleUpdateBotConfig,
    isUpdatingBotConfig: botConfigMutation.isPending,
    // AI loadout templates
    aiLoadoutTemplates,
    isAILoadoutLoading,
  };
}